<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchAccountProductsConfig, updateAccountProductsConfig } from '@/api/accountsClient'
import { VerifyAdmin, fetchAccounts } from '@/api/accountsAdmin'
import { useUserStore } from '@/stores/userStore'
import ProductHeader from '@/components/ProductHeader.vue'

interface Field {
  key: string
  type: string
  required: boolean
  arrayType?: 'string' | 'number' | 'object'
  objectProperties?: Field[]
  arrayObjectProperties?: Field[]
}

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const productName = ref('')
const fields = ref<Field[]>([])
const templateData = ref<Record<string, any>>({})
const sampleConfig = ref<Record<string, any>>({})
const productId = ref<string>(
  route.params.id === 'child-product' ? String(route.query.query) : String(route.params.id)
)
const Enabled = ref(true)

const loader = ref(true)
const submitLoader = ref(false)

const schema = reactive<{
  productName: string
  config: { type: string; properties: Record<string, any>; required: string[]; Enable: boolean }
}>({
  productName: '',
  config: {
    type: 'object',
    properties: {},
    required: [] as string[],
    Enable: true,
  },
})

const loadSchema = async () => {
  try {
    const schemaId =
      route.params.id === 'child-product' ? String(route.query.query) : String(route.params.id)

    const data = await fetchAccountProductsConfig(schemaId)

    schema.productName = String(productId.value)
    templateData.value = data?.commonData || {}
    sampleConfig.value = data?.sampleObj || {}
    schema.config.Enable = data.config.Enable
    const h = (data as any)?.config?.hierarchy || (data as any)?.sampleObj?.hierarchy
    if (h) (schema as any).hierarchy = h
    productName.value = data.config.productName

    fields.value = convertSchemaToFields(data.config.properties, data.config.required || [])
  } catch (error) {
    console.error('Failed to load schema:', error)
  } finally {
    loader.value = false
  }
}

const convertSchemaToFields = (
  properties: Record<string, any>,
  requiredKeys: string[]
): Field[] => {
  return Object.entries(properties).map(([key, field]) => {
    const newField: Field = {
      key,
      type: field.type,
      required: requiredKeys.includes(key),
    }

    if (field.type === 'array') {
      newField.arrayType = field.items.type
      if (field.items.type === 'object' && field.items.properties) {
        newField.arrayObjectProperties = convertSchemaToFields(
          field.items.properties,
          field.items.required || []
        )
      }
    } else if (field.type === 'object' && field.properties) {
      newField.objectProperties = convertSchemaToFields(field.properties, field.required || [])
    }

    return newField
  })
}

const addField = () => {
  fields.value.push({
    key: '',
    type: 'string',
    required: false,
    arrayType: 'string',
    objectProperties: [],
    arrayObjectProperties: [],
  })
}

const addObjectProperty = (field: Field) => {
  if (!field.objectProperties) {
    field.objectProperties = []
  }
  field.objectProperties.push({
    key: '',
    type: 'string',
    required: false,
  })
}

const addArrayObjectProperty = (field: Field) => {
  if (!field.arrayObjectProperties) {
    field.arrayObjectProperties = []
  }
  field.arrayObjectProperties.push({
    key: '',
    type: 'string',
    required: false,
  })
}

const removeField = (index: number) => {
  fields.value.splice(index, 1)
}

const removeObjectProperty = (field: Field, index: number) => {
  field.objectProperties?.splice(index, 1)
}

const removeArrayObjectProperty = (field: Field, index: number) => {
  field.arrayObjectProperties?.splice(index, 1)
}

const buildProperties = (fields: Field[], parentIsRequired: boolean = true) => {
  const properties: Record<string, any> = {}
  const required: string[] = []

  fields.forEach((field) => {
    if (!field.key) return

    let prop: Record<string, any> = { type: field.type }

    if (field.type === 'object' && field.objectProperties?.length) {
      const { properties: objProps, required: objRequired } = buildProperties(
        field.objectProperties,
        parentIsRequired && field.required
      )
      prop.properties = objProps
      if (objRequired.length && parentIsRequired && field.required) {
        prop.required = objRequired
      }
    }

    if (field.type === 'array') {
      if (field.arrayType === 'object' && field.arrayObjectProperties?.length) {
        const { properties: objProps, required: objRequired } = buildProperties(
          field.arrayObjectProperties,
          parentIsRequired && field.required
        )
        prop.items = {
          type: 'object',
          properties: objProps,
          ...(objRequired.length && parentIsRequired && field.required
            ? {
                required: objRequired,
              }
            : {}),
        }
      } else {
        prop.items = { type: field.arrayType }
      }
    }

    if (field.required && parentIsRequired) {
      required.push(field.key)
    }

    properties[field.key] = prop
  })

  return { properties, required }
}

const updateSchema = () => {
  const { properties, required } = buildProperties(fields.value)
  schema.config.properties = properties
  schema.config.required = required
}

const submitSchema = async () => {
  submitLoader.value = true
  try {
    submitLoader.value = true

    updateSchema()
    console.log(schema.config.Enable)
    delete (schema.config as any).Enable
    await updateAccountProductsConfig(schema)
    alert('Schema updated successfully!')
    router.push('/products')
    submitLoader.value = false
  } catch (error) {
    console.error('Failed to update schema:', error)
    alert('Failed to update schema')
    submitLoader.value = false
  } finally {
    submitLoader.value = false
  }
}
const schemaPreview = computed(() => JSON.stringify(schema, null, 2))
const formatSample = (val: unknown) =>
  typeof val === 'object' ? JSON.stringify(val, null, 2) : String(val)

const isChildProduct = computed(() => String(productId.value || '').includes('/'))

onMounted(async () => {
  await loadSchema()
  await loadAccountsForProduct()
  await VerifyAdmin()
})
// Accounts-by-product data
const accounts = ref<
  Array<{ name: string; folder: string; juid: string; is_active: boolean | null }>
>([])
const accountsLoading = ref(false)
const accountsLoaded = ref(false)
const accountSearch = ref('')
const accountSearchRef = ref()
const accountHeaders = [
  { title: 'Account Name', value: 'name', sortable: true },
  { title: 'JUID', value: 'juid', sortable: true },
  { title: 'Actions', value: 'actions', sortable: false, align: 'end' as const },
]

const filteredAccounts = computed(() => {
  const q = String(accountSearch.value ?? '')
    .trim()
    .toLowerCase()
  if (!q) return accounts.value
  return accounts.value.filter(
    (a) => (a.name || '').toLowerCase().includes(q) || (a.juid || '').toLowerCase().includes(q)
  )
})
const focusAccountSearch = () => {
  setTimeout(() => {
    try {
      accountSearchRef.value?.focus?.()
    } catch {}
  }, 0)
}

const loadAccountsForProduct = async () => {
  try {
    accountsLoading.value = true
    // Extract simple product ID from path (e.g., "soda_exports/auto_trader" -> "auto_trader")
    const fullPath = String(productId.value || '')
    const productKey = fullPath.split('/').pop() || fullPath
    const data = await fetchAccounts({ productNames: [productKey] })
    const accountList = data.accounts || data
    const sortedData = [...accountList].sort((a: any, b: any) => {
      const nameA = (a.accountNameFriendly || a.folderName || 'No Name').toUpperCase()
      const nameB = (b.accountNameFriendly || b.folderName || 'No Name').toUpperCase()
      return nameA.localeCompare(nameB)
    })
    accounts.value = sortedData.map((account: any) => ({
      name: account.accountNameFriendly || account.folderName || 'No Name',
      folder: account?.folderName,
      juid: account.juid,
      is_active: account.is_active === undefined ? null : account.is_active,
    }))
    accountsLoaded.value = true
  } catch (error) {
    console.error('Failed to load accounts for product:', error)
  } finally {
    accountsLoading.value = false
  }
}

const onAccountRowClick = (_event: any, row: any) => {
  const item = row?.item ?? row
  const raw = item?.raw ?? item
  const folder = raw?.folder ?? raw?.folderName
  if (folder) {
    router.push(`/account-admin/${folder}`)
  }
}

const redirectToDashboard = (juid: string) => {
  try {
    const url = String(
      ((templateData.value as any) || {}).dashboard ||
        ((sampleConfig.value as any) || {}).dashboard ||
        ''
    ).trim()
    if (!url || !useUserStore()?.token) return
    const baseUrl = url.replace(/\/+$/, '')
    const userStore = useUserStore()
    window.open(
      `${baseUrl}/?token=${userStore?.token}&juid=${juid}&role=${userStore?.role}&redirectionDo=true`,
      '_blank'
    )
  } catch {}
}

watch(productId, async () => {
  await loadAccountsForProduct()
})

watch(
  () => [route.params.id, route.query.query],
  async () => {
    const newProductId =
      route.params.id === 'child-product' ? String(route.query.query) : String(route.params.id)

    if (newProductId !== productId.value) {
      productId.value = newProductId
      loader.value = true
      await loadSchema()
    } else {
      loader.value = true
      await loadSchema()
    }
  },
  { deep: true }
)

watch(accountsLoading, (val, oldVal) => {
  if (oldVal && !val) {
    focusAccountSearch()
  }
})

watch(
  fields,
  () => {
    updateSchema()
  },
  { deep: true }
)

const userStore = useUserStore()
const userRole = ref(userStore?.role)
const canViewConfigTabs = computed(() =>
  ['company_suadmin', 'company_owner', 'company_admin', 'company_user'].includes(
    String(userRole.value)
  )
)
const canEditProductTemplate = computed(() =>
  ['company_suadmin', 'company_owner'].includes(String(userRole.value))
)
const goToEditCurrent = () => {
  const id = String(productId.value || '')
  if (!id) return
  if (id.includes('/')) {
    router.push({ path: '/products/edit/child-product', query: { query: id } })
  } else {
    router.push(`/products/edit/${id}`)
  }
}

const viewSchemaDialog = ref(false)
const schemaJson = computed(() => {
  return JSON.stringify(schema.config, null, 2)
})

const openViewSchema = () => {
  viewSchemaDialog.value = true
}

const copySchemaToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(schemaJson.value)
  } catch (err) {
    console.error('Failed to copy schema:', err)
  }
}

const goBackToProducts = () => {
  router.push('/products')
}
</script>

<template>
  <ProductHeader :productId="String(productId)" />

  <v-container>
    <div cols="12" class="subheader-sticky">
      <div
        style="
          display: flex !important;
          justify-content: space-between;
          flex-wrap: wrap;
          align-items: center;
        "
      >
        <div class="d-flex align-center" style="gap: 8px">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-arrow-left"
            class="text-grey-darken-2"
            @click="goBackToProducts"
          >
            Back
          </v-btn>
          <h2 class="text-h5 font-weight-medium" style="margin: 0">Product Information</h2>
        </div>
        <div v-if="canEditProductTemplate" class="d-flex ga-2">
          <v-btn
            data-testid="view-schema-btn"
            variant="text"
            size="small"
            prepend-icon="mdi-code-json"
            class="text-grey-darken-2"
            @click="openViewSchema"
          >
            View Schema
          </v-btn>
          <v-btn
            data-testid="edit-template-btn"
            variant="text"
            size="small"
            prepend-icon="mdi-pencil"
            class="text-grey-darken-2"
            @click="goToEditCurrent"
          >
            Edit template
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Product Clients -->
    <v-card flat class="mb-6">
      <v-card-title class="d-flex align-center">Clients</v-card-title>
      <v-card-text>
        <v-data-table
          data-testid="clients-table"
          :headers="accountHeaders"
          :items="filteredAccounts"
          :loading="accountsLoading"
          :items-per-page="50"
          class="elevation-1 sticky-header striped-rows"
          style="max-width: 100%; width: 100%; overflow-x: hidden"
          @click:row="onAccountRowClick"
        >
          <template #top>
            <!-- Desktop: compact, inline search on the right -->
            <div
              v-if="!$vuetify.display.mdAndDown"
              class="bg-grey-lighten-5 d-flex align-center pb-2"
              style="width: 100%; gap: 8px; padding: 8px 8px 0 8px"
            >
              <v-spacer></v-spacer>
              <v-text-field
                data-testid="client-search-input"
                ref="accountSearchRef"
                v-model="accountSearch"
                placeholder="Search accounts…"
                clearable
                @click:clear="accountSearch = ''"
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="compact"
                variant="solo"
                class="search-in-bar mr-2"
                style="max-width: 380px"
              ></v-text-field>
            </div>
            <!-- Mobile: full-width search on its own row -->
            <div v-else class="bg-grey-lighten-5 pb-2" style="width: 100%; padding: 8px 8px 0 8px">
              <v-text-field
                data-testid="client-search-input"
                ref="accountSearchRef"
                v-model="accountSearch"
                placeholder="Search accounts…"
                clearable
                @click:clear="accountSearch = ''"
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="comfortable"
                variant="solo"
                style="width: 100%; max-width: 100%"
              ></v-text-field>
            </div>
          </template>
          <template #item.is_active="{ item }">
            <div class="d-flex justify-start" style="width: 100%">
              <v-chip size="x-small" :color="item.is_active ? 'green' : 'red'" variant="tonal">
                {{ item.is_active ? 'Active' : 'Inactive' }}
              </v-chip>
            </div>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex align-center justify-end" style="width: 100%; gap: 6px">
              <v-btn
                v-if="(templateData as any)?.dashboard || (sampleConfig as any)?.dashboard"
                data-testid="open-dashboard-btn"
                :disabled="item?.is_active === false"
                variant="flat"
                color="primary"
                prepend-icon="mdi-open-in-new"
                @click.stop="redirectToDashboard(item?.juid)"
              >
                Open Dashboard
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>

  <!-- View Schema Dialog -->
  <v-dialog v-model="viewSchemaDialog" max-width="800" data-testid="view-schema-dialog">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="text-h6">Product Schema</span>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="viewSchemaDialog = false"
          data-testid="close-schema-dialog-btn"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div class="d-flex justify-end mb-2">
          <v-btn
            variant="text"
            size="small"
            prepend-icon="mdi-content-copy"
            @click="copySchemaToClipboard"
          >
            Copy to Clipboard
          </v-btn>
        </div>
        <v-sheet
          color="grey-lighten-5"
          rounded
          class="pa-4"
          style="max-height: 600px; overflow-y: auto"
          data-testid="schema-content"
        >
          <pre
            style="
              margin: 0;
              font-family: 'Courier New', monospace;
              font-size: 13px;
              line-height: 1.5;
              white-space: pre-wrap;
              word-wrap: break-word;
            "
            >{{ schemaJson }}</pre
          >
        </v-sheet>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="text"
          @click="viewSchemaDialog = false"
          data-testid="close-schema-btn"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.center {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
}

.schema-editor {
  max-width: 1200px;
  margin: 0 auto;
}

.div-circular {
  border: 2px solid red;
}

.schema-preview {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  overflow-x: auto;
  font-family: monospace;
}

.product-header {
  position: sticky;
  top: 0;
  z-index: 5;
}
@media (max-width: 960px) {
  .product-header {
    top: 0;
  }
}

.v-field__input {
  background-color: red !important;
}
.view-only .v-input__slot {
  pointer-events: none !important;
}
.view-only .v-field__label,
.view-only .v-field__input {
  opacity: 1 !important;
  color: rgba(0, 0, 0, 0.87) !important;
}
.status-text {
  font-weight: 600;
}
.status-active {
  color: #2e7d32;
}
.status-inactive {
  color: #c62828;
}
.striped-rows :deep(tbody tr) {
  cursor: pointer;
}
.striped-rows :deep(tbody tr:hover) {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
.subheader-sticky {
  position: sticky;
  top: 64px;
  z-index: 3;
  background: white;
}
@media (max-width: 960px) {
  .action-buttons {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 10px;
    width: 100%;
    flex-basis: 100%;
    margin-top: 6px;
  }
}
</style>
